// DEPENDENCIES ---------------------------------------------------------------- //

import { useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { useUser, setUser } from '@store';
import { authService, auth, buildGlobalUser, checkForPermittedRole } from '../services/authService';
import { reportProfileLoadFailure } from '../services/profileReport';
import { useRefreshUserClaims } from '../hooks/useRefreshUserClaims';
import { useUserPermissions, usePermissionsLoaded } from '@utils/featurePermissions';
import FeaturePermissionsPanel from '@features/userManagement/components/FeaturePermissionsPanel';
import RoleChip from '@features/userManagement/components/RoleChip';
import AppDownloadBadges from '@components/AppDownloadBadges';

import { css } from '@emotion/css';
import {
  Theme,
  useTheme,
  Avatar,
  Container,
  Paper,
  Typography,
  Link,
  TextField,
  Button,
  Alert,
  Tab,
  Tabs,
  Box,
} from '@mui/material';
import { BiLogOut, BiSolidEnvelope, BiSolidPhone, BiSolidUser, BiSolidLock, BiSolidCheckShield, BiSolidShield, BiRefresh } from 'react-icons/bi';

// TAB PANELS ---------------------------------------------------------------- //

const ProfileTab = () => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const navigate = useNavigate();
  const user = useUser();

  const handleLogout = async () => {
    try {
      await authService.writeUserEvent('user.logout');
    } catch (err) {
      console.log('Failed to write user event', err);
    }
    await authService.logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box>
        <Typography variant='body2' color='text.secondary' sx={{ marginBottom: '4px' }}>
          Role
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <RoleChip role={user?.profile?.default_role || ''} />
        </Box>
      </Box>

      {user?.profile?.email && (
        <Box>
          <Typography variant='body2' color='text.secondary' sx={{ marginBottom: '4px' }}>
            Email
          </Typography>
          <div className={cls.infoRow}>
            <BiSolidEnvelope className={cls.icon} size={18} />
            <Typography>{user.profile.email}</Typography>
          </div>
        </Box>
      )}

      {user?.profile?.phone && (
        <Box>
          <Typography variant='body2' color='text.secondary' sx={{ marginBottom: '4px' }}>
            Phone
          </Typography>
          <div className={cls.infoRow}>
            <BiSolidPhone className={cls.icon} size={18} />
            <Typography>{user.profile.phone}</Typography>
          </div>
        </Box>
      )}

      <Box>
        <Typography variant='body2' color='text.secondary' sx={{ marginBottom: '4px' }}>
          Member Since
        </Typography>
        <Typography>
          {dayjs(user?.profile?.created_at).format('MMMM D, YYYY')}
        </Typography>
      </Box>

      <Link
        component='button'
        onClick={() => navigate('/user-agreement')}
        sx={{ alignSelf: 'flex-start', fontSize: 14 }}
      >
        User Agreement
      </Link>

      <Box sx={{ borderTop: 1, borderColor: 'divider', paddingTop: '20px' }}>
        <Button
          variant='outlined'
          color='error'
          startIcon={<BiLogOut />}
          onClick={handleLogout}
        >
          Log Out
        </Button>
      </Box>
    </Box>
  );
};

const SecurityTab = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changeError, setChangeError] = useState<string | null>(null);
  const [changeSuccess, setChangeSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    setChangeError(null);
    setChangeSuccess(false);

    if (newPassword !== confirmPassword) {
      setChangeError('New passwords do not match.');
      return;
    }

    setChangingPassword(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setChangeSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const code = err?.code;
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setChangeError('Current password is incorrect.');
      } else if (code === 'auth/weak-password') {
        setChangeError('New password is too weak. Please use at least 6 characters.');
      } else {
        setChangeError(err?.message || 'Failed to change password.');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Box>
      <Typography variant='subtitle1' sx={{ fontWeight: 600, marginBottom: '16px' }}>
        Change Password
      </Typography>

      {changeError && (
        <Alert severity='error' sx={{ marginBottom: '16px' }}>
          {changeError}
        </Alert>
      )}
      {changeSuccess && (
        <Alert severity='success' sx={{ marginBottom: '16px' }}>
          Password changed successfully.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: 400 }}>
        <TextField
          fullWidth
          type='password'
          label='Current Password'
          value={currentPassword}
          onChange={e => setCurrentPassword(e.target.value)}
        />
        <TextField
          fullWidth
          type='password'
          label='New Password'
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
        />
        <TextField
          fullWidth
          type='password'
          label='Confirm New Password'
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
        />
        <Button
          variant='contained'
          color='primary'
          disabled={!currentPassword || !newPassword || !confirmPassword || changingPassword}
          onClick={handleChangePassword}
          sx={{ alignSelf: 'flex-start' }}
        >
          {changingPassword ? 'Changing...' : 'Change Password'}
        </Button>
      </Box>
    </Box>
  );
};

const UserAccessTab = () => {
  const theme = useTheme();
  const user = useUser();
  const permissions = useUserPermissions();
  const permissionsLoaded = usePermissionsLoaded();
  const { refreshUserClaims } = useRefreshUserClaims();

  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState<'success' | 'error' | null>(null);

  const allowedRoles = user?.claims?.['x-hasura-allowed-roles'] || [];
  const defaultRole = user?.claims?.['x-hasura-default-role'] || '';

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      // Bump updated_at to trigger backend Firebase claim regeneration
      const userId = user?.claims?.['x-hasura-user-id'];
      if (userId) await refreshUserClaims(userId);

      // Force Firebase to fetch a fresh token with updated claims
      await auth.currentUser?.getIdToken(true);
      // Rebuild the user object (also reloads feature permissions)
      const { user: globalUser, profileFailure, diagnostics } = await buildGlobalUser();

      const permittedRole = checkForPermittedRole(globalUser);
      if (!permittedRole) {
        setRefreshResult('error');
        return;
      }

      // Keep the loaded user rather than replace it with one that has no profile.
      if (!globalUser.profile) {
        reportProfileLoadFailure({
          phase: 'profile_page_refresh',
          user: globalUser,
          failure: profileFailure,
          diagnostics,
          firebaseUid: auth.currentUser?.uid,
          attempts: 1,
          action: 'kept_existing_user',
        });
        setRefreshResult('error');
        return;
      }

      setUser(globalUser);
      setRefreshResult('success');
    } catch (err) {
      console.error('Failed to refresh permissions', err);
      setRefreshResult('error');
    } finally {
      setRefreshing(false);
    }
  };

  // Sort roles so default role appears first
  const sortedRoles = [...allowedRoles].sort((a: string, b: string) => {
    if (a === defaultRole) return -1;
    if (b === defaultRole) return 1;
    return 0;
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Roles Section */}
      <Box>
        <Box display='flex' alignItems='center' gap={1} mb={2}>
          <BiSolidShield size={24} color={theme.palette.primary.main} />
          <Typography variant='h6' fontWeight={600}>
            Roles
          </Typography>
        </Box>

        {sortedRoles.length === 0 ? (
          <Typography color='text.secondary'>No roles assigned</Typography>
        ) : (
          <Box display='flex' flexWrap='wrap' gap={1}>
            {sortedRoles.map((role: string) => (
              <RoleChip key={role} role={role} />
            ))}
          </Box>
        )}
      </Box>

      {/* Mobile App */}
      {allowedRoles.includes('driver') && (
        <Box>
          <AppDownloadBadges compact label="HopDrive Driver App" />
        </Box>
      )}

      {/* Feature Permissions Section */}
      <Box>
        <Box display='flex' alignItems='center' gap={1} mb={2}>
          <BiSolidCheckShield size={24} color={theme.palette.primary.main} />
          <Typography variant='h6' fontWeight={600}>
            Feature Permissions
          </Typography>
        </Box>

        {!permissionsLoaded ? (
          <Typography color='text.secondary'>Loading permissions...</Typography>
        ) : permissions.length === 0 ? (
          <Typography color='text.secondary'>No feature permissions assigned</Typography>
        ) : (
          <FeaturePermissionsPanel
            selectedNames={permissions}
            readOnly
          />
        )}

        <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
          These permissions determine which features you can access in the application.
          Contact your administrator if you need additional permissions.
        </Typography>
      </Box>

      {/* Refresh Section */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', paddingTop: '20px', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant='outlined'
          size='small'
          startIcon={<BiRefresh />}
          disabled={refreshing}
          onClick={handleRefresh}
        >
          {refreshing ? 'Refreshing...' : 'Refresh Permissions'}
        </Button>
        {refreshResult === 'success' && (
          <Alert severity='success' sx={{ py: 0, flex: 1 }}>
            Permissions refreshed successfully.
          </Alert>
        )}
        {refreshResult === 'error' && (
          <Alert severity='error' sx={{ py: 0, flex: 1 }}>
            Failed to refresh permissions.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

// COMPONENT ---------------------------------------------------------------- //

const ProfilePage = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const user = useUser();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Container maxWidth='sm' sx={{ paddingTop: '24px', paddingBottom: '24px' }}>
      <Paper variant='custom' sx={{ position: 'relative', overflow: 'hidden' }}>
        {/* Header banner */}
        <div className={cls.profileTop}>
          <Typography sx={{ fontSize: 20, fontWeight: 300 }}>Welcome,</Typography>
          <Typography sx={{ fontSize: 40, fontWeight: 500 }}>
            {user?.profile?.display_name || 'Unknown User'}
          </Typography>
        </div>

        {/* Avatar */}
        <div className={cls.profileImage}>
          <Avatar
            alt={user?.profile?.display_name || 'User'}
            src={user?.profile?.avatar_url || ''}
            sx={{
              width: 128,
              height: 128,
              backgroundColor: theme?.palette?.utility?.lighter,
            }}
          >
            <BiSolidUser size={72} />
          </Avatar>
          <div className={cls.profileImageShadow} />
        </div>

        {/* Spacer for avatar overflow */}
        <Box sx={{ height: 80 }} />

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_e, val) => setActiveTab(val)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: '24px' }}
        >
          <Tab icon={<BiSolidUser size={16} />} iconPosition='start' label='Profile' sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }} />
          <Tab icon={<BiSolidLock size={16} />} iconPosition='start' label='Security' sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }} />
          <Tab icon={<BiSolidCheckShield size={16} />} iconPosition='start' label='User Access' sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }} />
        </Tabs>

        {/* Tab content */}
        <Box sx={{ padding: '24px' }}>
          {activeTab === 0 && <ProfileTab />}
          {activeTab === 1 && <SecurityTab />}
          {activeTab === 2 && <UserAccessTab />}
        </Box>
      </Paper>
    </Container>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  profileTop: css`
    position: relative;
    width: 100%;
    height: 192px;
    padding-top: 36px;
    text-align: center;
    background-color: ${theme?.palette?.secondary?.darker};
    color: ${theme?.palette?.text?.contrastPrimary};
  `,

  profileImage: css`
    position: absolute;
    top: 128px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
  `,
  profileImageShadow: css`
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    box-shadow: 0px 0px 16px 2px #00000030 inset;
    cursor: pointer;
  `,

  infoRow: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,

  icon: css`
    color: ${theme?.palette?.text?.secondary};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default ProfilePage;
